import React, { useState, useEffect } from 'react';
import { useData } from '../../../../../hooks/useData';
import { TABLES } from '../../../../../constants/supabaseConstants';
import { validateRequired } from '../../../../../utils/validation';
import styles from '../../WizardSteps.module.css';

const ProjectInfoStep = ({ data, onChange }) => {
  // Fetch reference data
  const { data: constructionTypes, isLoading: isLoadingTypes } = useData(
    TABLES.REFERENCE_CONSTRUCTION_TYPES,
  );
  const { data: projectPhases, isLoading: isLoadingPhases } = useData(
    TABLES.REFERENCE_PROJECT_PHASES,
  );

  // Initialize with existing data or defaults
  const [formData, setFormData] = useState({
    projectName: data?.projectName || '',
    address: data?.address || '',
    constructionTypeId: data?.constructionTypeId || '',
    startDate: data?.startDate || new Date().toISOString().split('T')[0],
    projectPhaseId: data?.projectPhaseId || '',
    totalBudget: data?.totalBudget || '',
  });

  const [errors, setErrors] = useState({});

  // Update parent when form data changes
  useEffect(() => {
    onChange(formData);
  }, [formData, onChange]);

  const handleChange = field => e => {
    let value = e.target.value;

    // For budget field, strip non-numeric chars (except decimal) before storing
    if (field === 'totalBudget') {
      value = value.replace(/[^0-9.]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Format budget value with thousand separators for display
  const formatBudgetDisplay = value => {
    if (!value) return '';
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString('he-IL');
  };

  // Validate field on blur
  const handleBlur = field => () => {
    let error = null;

    switch (field) {
      case 'address':
        error = validateRequired(formData.address, 'כתובת');
        break;
      case 'constructionTypeId':
        error = validateRequired(formData.constructionTypeId, 'סוג בנייה');
        break;
      case 'startDate':
        error = validateRequired(formData.startDate, 'תאריך התחלה');
        break;
      case 'projectPhaseId':
        error = validateRequired(formData.projectPhaseId, 'סטטוס פרויקט');
        break;
      case 'totalBudget':
        // Budget is optional, but if provided must be a valid number
        if (formData.totalBudget && isNaN(Number(formData.totalBudget))) {
          error = 'תקציב חייב להיות מספר';
        }
        break;
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>פרטי הפרויקט</h1>
        <p className={styles.subtitle}>ספר לנו על הפרויקט שלך</p>
      </div>

      {/* Form Fields */}
      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label}>
            שם הפרויקט <span className={styles.optional}>(אופציונלי)</span>
          </label>
          <input
            type="text"
            className={styles.input}
            value={formData.projectName}
            onChange={handleChange('projectName')}
            placeholder="למשל: הדירה בתל אביב"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>כתובת הפרויקט</label>
          <input
            type="text"
            className={`${styles.input} ${errors.address ? styles.inputError : ''}`}
            value={formData.address}
            onChange={handleChange('address')}
            onBlur={handleBlur('address')}
            placeholder="הכנס כתובת"
          />
          {errors.address && <span className={styles.error}>{errors.address}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>סוג בנייה</label>
          <select
            className={`${styles.input} ${styles.select} ${errors.constructionTypeId ? styles.inputError : ''}`}
            value={formData.constructionTypeId}
            onChange={handleChange('constructionTypeId')}
            onBlur={handleBlur('constructionTypeId')}
            disabled={isLoadingTypes}
          >
            <option value="">{isLoadingTypes ? 'טוען...' : 'בחר סוג בנייה'}</option>
            {constructionTypes?.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.constructionTypeId && (
            <span className={styles.error}>{errors.constructionTypeId}</span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>תאריך התחלה משוער</label>
          <input
            type="date"
            className={`${styles.input} ${errors.startDate ? styles.inputError : ''}`}
            value={formData.startDate}
            onChange={handleChange('startDate')}
            onBlur={handleBlur('startDate')}
          />
          {errors.startDate && <span className={styles.error}>{errors.startDate}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>סטטוס נוכחי</label>
          <select
            className={`${styles.input} ${styles.select} ${errors.projectPhaseId ? styles.inputError : ''}`}
            value={formData.projectPhaseId}
            onChange={handleChange('projectPhaseId')}
            onBlur={handleBlur('projectPhaseId')}
            disabled={isLoadingPhases}
          >
            <option value="">{isLoadingPhases ? 'טוען...' : 'בחר סטטוס'}</option>
            {projectPhases?.map(phase => (
              <option key={phase.id} value={phase.id}>
                {phase.name}
              </option>
            ))}
          </select>
          {errors.projectPhaseId && <span className={styles.error}>{errors.projectPhaseId}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            תקציב משוער <span className={styles.optional}>(אופציונלי)</span>
          </label>
          <div className={styles.currencyWrapper}>
            <input
              type="text"
              inputMode="numeric"
              className={`${styles.input} ${styles.currencyInput} ${errors.totalBudget ? styles.inputError : ''}`}
              value={formatBudgetDisplay(formData.totalBudget)}
              onChange={handleChange('totalBudget')}
              onBlur={handleBlur('totalBudget')}
              placeholder="הכנס תקציב"
            />
            <span className={styles.currencySymbol}>₪</span>
          </div>
          {errors.totalBudget && <span className={styles.error}>{errors.totalBudget}</span>}
        </div>
      </div>
    </div>
  );
};

export default ProjectInfoStep;
